import React, { useState, useRef, useEffect } from "react";
import { CloseButton, Spinner, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faExchangeAlt,
  faPlus,
  faArrowsAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useUploadMenuItemLogoFileExtntionHttpsMutation } from "../../redux/services/menuItemService";
import { uploadImage } from "../imageUploadCompont";
import "./index.css";

export type UpdateImageDetails = {
  oldImgUrl: string;
  newImgRef?: string | null; // optional: omit newImgRef for deletions
};

type Props = {
  exsistImages: string[];
    uploadImageCallBack: (details: UpdateImageDetails) => void;
  onOrderChange?: (orderedRefs: string[]) => void; // NEW for reorder
  isDisable?: boolean;
};

type ImageData = {
  ref: string;
  previewUrl: string;
};

const MenuItemImageUpdate = ({
  exsistImages,
  uploadImageCallBack,
  onOrderChange,
  isDisable,
}: Props) => {
  const [images, setImages] = useState<ImageData[]>([]);
  // Track the original filename for each slot (by index) so we always know the true oldImgUrl even after changes
  const [originalFilenamesMap, setOriginalFilenamesMap] = useState<Map<number, string>>(new Map());
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // pendingImageContext now also stores the index of the image being changed
  const [pendingImageContext, setPendingImageContext] = useState<{
    type: "new" | "change";
    url?: string;
    index?: number; // index of the image being changed (for stable oldImgUrl lookup)
  } | null>(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [uploadMenuItemFileExtetion] =
    useUploadMenuItemLogoFileExtntionHttpsMutation();

  useEffect(() => {
    const initialized = (exsistImages || []).map((url) => ({
      ref: url,
      previewUrl: url,
    }));
    setImages(initialized);
    // Build map of original filenames keyed by index
    const map = new Map<number, string>();
    (exsistImages || []).forEach((url, idx) => {
      const filename = extractImageFilename(url);
      map.set(idx, filename);
    });
    setOriginalFilenamesMap(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - we want stable original filenames

  // Return the filename (last path segment) without any query string or fragment, decoded.
  const extractImageFilename = (url: string) => {
    if (!url) return url;
    try {
      const lastSegment = url.split("/").pop() || url;
      // strip query string / fragment
      const clean = lastSegment.split(/[?#]/)[0];
      return decodeURIComponent(clean);
    } catch {
      return url;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    setSelectedFile(file);
    const fileReader = new FileReader();
    fileReader.onload = () => {
      setSelectedFilePreview(fileReader.result as string);
    };
    fileReader.readAsDataURL(file);
  };

  const handleConfirmImage = async () => {
    if (!selectedFile || !pendingImageContext) return;

    const fileExt = selectedFile.name.split(".").pop()?.toLowerCase() || "jpg";

    try {
      setIsUploading(true);
      const newImgRef = await uploadImage(uploadMenuItemFileExtetion, {
        fileExtention: fileExt,
        formData: selectedFile,
      });

      const localPreviewUrl = URL.createObjectURL(selectedFile);

      if (pendingImageContext.type === "change" && pendingImageContext.url !== undefined) {
        // Look up the true original filename from our stable map using the index
        const idx = pendingImageContext.index ?? -1;
        const oldFilename = originalFilenamesMap.get(idx);
        
        if (!oldFilename) {
          console.error("Could not find original filename for index", idx);
          setIsUploading(false);
          return;
        }

        // Normalize new image ref (strip query/fragments) before sending to parent
        const newFilename = extractImageFilename(newImgRef);
        
        
        uploadImageCallBack({
          oldImgUrl: oldFilename,
          newImgRef: newFilename,
        });

        setImages((prev) =>
          prev.map((img) =>
            // replace the current slot with the new ref (keep preview as local object URL)
            img.ref === pendingImageContext.url
              ? { ref: newImgRef, previewUrl: localPreviewUrl }
              : img
          )
        );
      } else {
        // New image: oldImgUrl empty and newImgRef present
        const newFilename = extractImageFilename(newImgRef);
        uploadImageCallBack({ oldImgUrl: "", newImgRef: newFilename });
        setImages((prev) => [...prev, { ref: newImgRef, previewUrl: localPreviewUrl }]);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
      setPendingImageContext(null);
      setSelectedFile(null);
      setSelectedFilePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCancelPreview = () => {
    setSelectedFile(null);
    setSelectedFilePreview(null);
    setPendingImageContext(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFileInput = (type: "new" | "change", url?: string, index?: number) => {
    if (isDisable) return;
    setPendingImageContext({ type, url, index });
    fileInputRef.current?.click();
  };

  const handleDeleteImage = (url: string) => {
    // For deletion, send only the oldImgUrl (no newImgRef) so parent treats this as a delete-only event
    uploadImageCallBack({
      oldImgUrl: extractImageFilename(url),
      // newImgRef intentionally omitted for delete
    });
    setImages((prev) => prev.filter((img) => img.ref !== url));
  };

  const handleAddNewImage = () => {
    if (isDisable || images.length >= 5) return;
    triggerFileInput("new");
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (isDisable) return;
    e.dataTransfer.setData("text/plain", index.toString());
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetIndex: number) => {
    if (isDisable) return;
    e.preventDefault();
    const sourceIndex = Number(e.dataTransfer.getData("text/plain"));

    if (sourceIndex !== targetIndex) {
      const newImages = [...images];
      const [movedImage] = newImages.splice(sourceIndex, 1);
      newImages.splice(targetIndex, 0, movedImage);
      setImages(newImages);
      onOrderChange?.(newImages.map((i) => i.ref)); // report order change
    }

    setDraggedIndex(null);
  };

  return (
    <div className="image-update-container">
      <h4 className="mb-4">Images</h4>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />
      {selectedFilePreview && (
        <div className="image-preview-overlay">
          <div className="preview-content">
            <img src={selectedFilePreview} alt="New Preview" />
            <div className="d-flex justify-content-between mt-2">
              <Button variant="success" onClick={handleConfirmImage}>
                Confirm
              </Button>
              <Button variant="secondary" onClick={handleCancelPreview}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          className="image-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <div className="preview-content">
            <img src={previewImage} alt="Preview" />
            <CloseButton
              className="preview-close"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewImage(null);
              }}
            />
          </div>
        </div>
      )}

      <div className="image-grid">
        {images.map((img, index) => (
          <div
            key={index}
            className={`image-card ${draggedIndex === index ? "dragging" : ""}`}
            draggable={!isDisable}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div
              className="image-container"
              onClick={() => setPreviewImage(img.previewUrl)}
            >
              <img src={img.previewUrl} alt={`Menu item ${index + 1}`} />
              {!isDisable && (
                <div className="image-actions">
                  <button
                    className="action-btn change-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFileInput("change", img.ref, index);
                    }}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(img.ref);
                    }}
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                  <div className="drag-handle">
                    <FontAwesomeIcon icon={faArrowsAlt} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {!isDisable && images.length < 5 && (
          <div className="image-card add-new" onClick={handleAddNewImage}>
            <div className="add-new-content">
              <FontAwesomeIcon icon={faPlus} size="2x" />
              <span>Add Image</span>
            </div>
          </div>
        )}
      </div>

      {isUploading && (
        <div className="upload-status">
          <Spinner animation="border" variant="primary" className="me-2" />
          <span>Uploading image...</span>
        </div>
      )}

      <div className="image-notes mt-3">
        <p>
          <small>
            • Maximum 5 images allowed
            <br />
            • Drag to reorder images (first image will be primary)
            <br />
            • Recommended size: 800x600px
          </small>
        </p>
      </div>
    </div>
  );
};

export default MenuItemImageUpdate;


